import AcUnitIcon from '@mui/icons-material/AcUnit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { alpha, Box, Link, Stack, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { getWeatherCodeData } from '/@/components/field-list-v2/components/sidebar/components/weather-mini-card/weather-codes';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import { useWeatherForecast } from '/@/hooks/frost-risk';
import { _ } from '/@/locale';
import Alert from '../bayer/alert';
import ModalV2 from '../bayer/modal-v2';
import { WEATHER_MODAL_COLORS } from './colors';
import { buildDailyRows, buildHourlyRows, hasFrostRisk } from './data';
import ForecastTable from './forecast-table';

type WeatherTab = 'hourly' | 'extended';

interface WeatherStatusModalProps {
    open: boolean;
    onClose: () => void;
    lat: number;
    lng: number;
    /** Header subtitle, e.g. "Marcos Juarez, Córdoba". */
    locationName?: string;
    /** Called by the "Ver mapa de heladas" link; the link is hidden when omitted. */
    onOpenFrostMap?: () => void;
}

const Tab = ({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) => {
    const theme = useTheme();
    return (
        <Box
            component='button'
            type='button'
            onClick={onClick}
            sx={{
                border: 'none',
                cursor: 'pointer',
                height: 32,
                px: 1.5,
                borderRadius: 1,
                bgcolor: active ? alpha(theme.palette.secondary.main, 0.14) : alpha(theme.palette.primary.main, 0.12),
                color: active ? theme.palette.secondary.main : theme.palette.primary.main,
            }}
        >
            <Typography variant='body2' component='span'>
                {label}
            </Typography>
        </Box>
    );
};

const WeatherStatusModal = ({ open, onClose, lat, lng, locationName, onOpenFrostMap }: WeatherStatusModalProps) => {
    const theme = useTheme();
    const units = useUnitProvider();
    const [tab, setTab] = useState<WeatherTab>('hourly');

    const weatherForecastQuery = useWeatherForecast({ lat, lng });
    const data = weatherForecastQuery.data?.data;
    const primary = theme.palette.primary.main;

    const current = data?.current?.data;
    const weatherCodeData = useMemo(() => getWeatherCodeData(current?.currentIcon), [current?.currentIcon]);

    const { minTemp, maxTemp } = useMemo(() => {
        const today = new Date().toISOString().split('T')[0];
        const todayForecast = data?.forecast?.find((f) => f.date === today)?.data?.[0];
        if (!todayForecast) return { minTemp: undefined, maxTemp: undefined };
        return {
            minTemp: Math.min(todayForecast.day.minTemp, todayForecast.night.minTemp),
            maxTemp: Math.max(todayForecast.day.maxTemp, todayForecast.night.maxTemp),
        };
    }, [data?.forecast]);

    const hourlyRows = useMemo(() => (data ? buildHourlyRows(data, units) : []), [data, units]);
    const dailyRows = useMemo(() => (data ? buildDailyRows(data, units) : []), [data, units]);

    const conditionText = weatherCodeData?.description ? _(weatherCodeData.description) : current?.currentText;
    const currentTempDeg = current ? `${units.toTemperature(current.currentTemp, 0).value}°` : null;
    const minDeg = minTemp !== undefined ? `${units.toTemperature(minTemp, 0).value}°` : null;
    const maxDeg = maxTemp !== undefined ? `${units.toTemperature(maxTemp, 0).value}°` : null;

    const showFrost = data ? hasFrostRisk(data) : false;
    const tempUnit = units.toTemperature(0, 0).unit;
    const speedUnit = units.toSpeed(0, 0).unit;

    return (
        <ModalV2
            open={open}
            onClose={onClose}
            title={_('Estado del tiempo')}
            maxWidth='sm'
            bodyProps={{ alignItems: 'stretch', textAlign: 'left' }}
        >
            {weatherForecastQuery.isValidating && !data ? (
                <Stack justifyContent='center' alignItems='center' sx={{ py: 6 }}>
                    <Spinner />
                </Stack>
            ) : !data ? (
                <Stack justifyContent='center' alignItems='center' sx={{ py: 6 }}>
                    <Typography variant='body1' color='primary'>
                        {_('No hay datos disponibles')}
                    </Typography>
                </Stack>
            ) : (
                <Stack spacing={2} sx={{ width: '100%' }}>
                    {/* Location */}
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                            {_('Hoy')}
                        </Typography>
                        {locationName && (
                            <Stack direction='row' alignItems='center' spacing={0.5}>
                                <LocationOnIcon sx={{ fontSize: 24, color: 'primary.onSurface' }} />
                                <Typography variant='body2' color='primary.mui.inactive'>
                                    {locationName}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {/* Current conditions */}
                    <Stack
                        alignItems='center'
                        spacing={1}
                        sx={{ bgcolor: WEATHER_MODAL_COLORS.surfaceVariant, borderRadius: 1, p: 2 }}
                    >
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Typography variant='h3' color='primary'>
                                {currentTempDeg ?? '—'}
                            </Typography>
                            {weatherCodeData?.image && (
                                <Box
                                    component='img'
                                    src={weatherCodeData.image}
                                    alt={typeof conditionText === 'string' ? conditionText : ''}
                                    sx={{ width: 32, height: 32 }}
                                />
                            )}
                        </Stack>
                        <Typography variant='body2' color='primary'>
                            {conditionText}
                            {minDeg && maxDeg && ` | ${_('Min')} ${minDeg} ${_('Max')} ${maxDeg}`}
                        </Typography>
                    </Stack>

                    {/* Tabs */}
                    <Stack direction='row' spacing={1}>
                        <Tab label={_('Próximas horas')} active={tab === 'hourly'} onClick={() => setTab('hourly')} />
                        <Tab label={_('Extendido')} active={tab === 'extended'} onClick={() => setTab('extended')} />
                    </Stack>

                    {/* Table */}
                    {tab === 'hourly' ? (
                        <ForecastTable
                            firstColLabel={_('Hora')}
                            rows={hourlyRows}
                            tempUnit={tempUnit}
                            speedUnit={speedUnit}
                        />
                    ) : (
                        <ForecastTable
                            firstColLabel={_('Día')}
                            rows={dailyRows}
                            tempUnit={tempUnit}
                            speedUnit={speedUnit}
                        />
                    )}

                    {/* Frost-risk footer */}
                    {showFrost && (
                        <Alert
                            severity='danger'
                            icon={<AcUnitIcon sx={{ fontSize: 24 }} />}
                            sx={{
                                '& .MuiAlert-message': {
                                    width: '100%',
                                    justifyContent: 'space-between',
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <span>{_('Riesgo de heladas en los próx. 7 días')}</span>
                            {onOpenFrostMap && (
                                <Link
                                    component='button'
                                    type='button'
                                    variant='body2'
                                    onClick={onOpenFrostMap}
                                    sx={{ color: primary, textDecorationColor: primary }}
                                >
                                    {_('Ver mapa de heladas')}
                                </Link>
                            )}
                        </Alert>
                    )}
                </Stack>
            )}
        </ModalV2>
    );
};

export default WeatherStatusModal;
